import React, { useState, useEffect, useContext } from 'react';
import { humanizeDates } from '@/lib/dates';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { PlusCircle, Eye, Star, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const EventServiceSurveyManagement = () => {
  const { token } = useContext(AuthContext);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    job_name: '',
    trade_type: '',
    service_description: '',
    service_location_name: '',
    service_address: '',
    service_city: '',
    service_state: '',
    service_zip: '',
    event_date: '',
    survey_date: '',
    survey_conducted_by: '',
    main_point_of_contact: '',
    overall_service_rating: 5,
    staff_courteous_rating: 5,
    needs_met_rating: 5,
    what_did_you_like_1: '',
    what_did_you_like_2: '',
    what_did_you_like_3: '',
    what_to_improve: '',
    general_comments: ''
  });

  // Fetch surveys on component mount
  useEffect(() => {
    fetchSurveys();
  }, [token]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/event-service-surveys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurveys(response.data || []);
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
      toast.error('Failed to load surveys');
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      job_name: '',
      trade_type: '',
      service_description: '',
      service_location_name: '',
      service_address: '',
      service_city: '',
      service_state: '',
      service_zip: '',
      event_date: '',
      survey_date: '',
      survey_conducted_by: '',
      main_point_of_contact: '',
      overall_service_rating: 5,
      staff_courteous_rating: 5,
      needs_met_rating: 5,
      what_did_you_like_1: '',
      what_did_you_like_2: '',
      what_did_you_like_3: '',
      what_to_improve: '',
      general_comments: ''
    });
  };

  const handleAdd = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleView = (survey) => {
    setSelectedSurvey(survey);
    setShowViewDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.customer_name || !formData.job_name || !formData.trade_type || 
        !formData.event_date || !formData.survey_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/event-service-surveys`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Survey submitted successfully');
      setShowAddDialog(false);
      fetchSurveys();
      resetForm();
    } catch (error) {
      console.error('Failed to submit survey:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit survey');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const filteredSurveys = surveys.filter(survey => 
    survey.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    survey.job_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    survey.trade_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        size={16} 
        className={i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} 
      />
    ));
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="p-4 space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Event Service Follow-up Surveys</h1>
          <p className="text-sm text-gray-600 mt-1">Track and manage customer satisfaction surveys</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchSurveys} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleAdd} className="bg-[#A8D5BA] hover:bg-[#8FC9A9] text-gray-800">
            <PlusCircle className="mr-2" size={18} />
            New Survey
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-4">
          <Input
            placeholder="Search by customer, job name, or trade type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </CardContent>
      </Card>

      {/* Surveys Table */}
      <Card>
        <CardContent className="pt-4">
          {filteredSurveys.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No surveys found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Survey Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Job Name</TableHead>
                    <TableHead>Trade Type</TableHead>
                    <TableHead>Overall Rating</TableHead>
                    <TableHead>Conducted By</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSurveys.map((survey) => (
                    <TableRow key={survey.id}>
                      <TableCell>{survey.survey_date || 'N/A'}</TableCell>
                      <TableCell className="font-medium">{survey.customer_name}</TableCell>
                      <TableCell>{humanizeDates(survey.job_name)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{survey.trade_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {renderStars(survey.overall_service_rating)}
                          <span className="ml-2 text-sm text-gray-600">
                            {survey.overall_service_rating}/5
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{survey.survey_conducted_by || 'N/A'}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(survey)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Eye size={16} className="mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Survey Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Event Service Follow-up Survey</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Event Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Event Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer_name">Customer Name *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => handleInputChange('customer_name', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="job_name">Job Name *</Label>
                  <Input
                    id="job_name"
                    value={formData.job_name}
                    onChange={(e) => handleInputChange('job_name', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="trade_type">Trade Type *</Label>
                  <Input
                    id="trade_type"
                    value={formData.trade_type}
                    onChange={(e) => handleInputChange('trade_type', e.target.value)}
                    placeholder="e.g., Event Cleaning, Staffing, Floor Care"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="event_date">Event Date *</Label>
                  <Input
                    id="event_date"
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => handleInputChange('event_date', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="service_description">Service Description</Label>
                <Textarea
                  id="service_description"
                  value={formData.service_description}
                  onChange={(e) => handleInputChange('service_description', e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            {/* Service Location */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Service Location</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="service_location_name">Location Name</Label>
                  <Input
                    id="service_location_name"
                    value={formData.service_location_name}
                    onChange={(e) => handleInputChange('service_location_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="service_address">Address</Label>
                  <Input
                    id="service_address"
                    value={formData.service_address}
                    onChange={(e) => handleInputChange('service_address', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="service_city">City</Label>
                  <Input
                    id="service_city"
                    value={formData.service_city}
                    onChange={(e) => handleInputChange('service_city', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="service_state">State</Label>
                  <Input
                    id="service_state"
                    value={formData.service_state}
                    onChange={(e) => handleInputChange('service_state', e.target.value)}
                    placeholder="e.g., FL"
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label htmlFor="service_zip">ZIP Code</Label>
                  <Input
                    id="service_zip"
                    value={formData.service_zip}
                    onChange={(e) => handleInputChange('service_zip', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Survey Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Survey Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="survey_date">Survey Date *</Label>
                  <Input
                    id="survey_date"
                    type="date"
                    value={formData.survey_date}
                    onChange={(e) => handleInputChange('survey_date', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="survey_conducted_by">Conducted By</Label>
                  <Input
                    id="survey_conducted_by"
                    value={formData.survey_conducted_by}
                    onChange={(e) => handleInputChange('survey_conducted_by', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="main_point_of_contact">Main Point of Contact</Label>
                  <Input
                    id="main_point_of_contact"
                    value={formData.main_point_of_contact}
                    onChange={(e) => handleInputChange('main_point_of_contact', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Ratings */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Service Ratings (1-5)</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="overall_service_rating">Overall Service Rating *</Label>
                  <Input
                    id="overall_service_rating"
                    type="number"
                    min="1"
                    max="5"
                    value={formData.overall_service_rating}
                    onChange={(e) => handleInputChange('overall_service_rating', parseInt(e.target.value))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="staff_courteous_rating">Staff Courteous and Friendly *</Label>
                  <Input
                    id="staff_courteous_rating"
                    type="number"
                    min="1"
                    max="5"
                    value={formData.staff_courteous_rating}
                    onChange={(e) => handleInputChange('staff_courteous_rating', parseInt(e.target.value))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="needs_met_rating">Needs Met Effectively *</Label>
                  <Input
                    id="needs_met_rating"
                    type="number"
                    min="1"
                    max="5"
                    value={formData.needs_met_rating}
                    onChange={(e) => handleInputChange('needs_met_rating', parseInt(e.target.value))}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Feedback */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Customer Feedback</h3>
              <div>
                <Label htmlFor="what_did_you_like_1">What did you like about the service? (1)</Label>
                <Textarea
                  id="what_did_you_like_1"
                  value={formData.what_did_you_like_1}
                  onChange={(e) => handleInputChange('what_did_you_like_1', e.target.value)}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="what_did_you_like_2">What did you like about the service? (2)</Label>
                <Textarea
                  id="what_did_you_like_2"
                  value={formData.what_did_you_like_2}
                  onChange={(e) => handleInputChange('what_did_you_like_2', e.target.value)}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="what_did_you_like_3">What did you like about the service? (3)</Label>
                <Textarea
                  id="what_did_you_like_3"
                  value={formData.what_did_you_like_3}
                  onChange={(e) => handleInputChange('what_did_you_like_3', e.target.value)}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="what_to_improve">What could we have improved?</Label>
                <Textarea
                  id="what_to_improve"
                  value={formData.what_to_improve}
                  onChange={(e) => handleInputChange('what_to_improve', e.target.value)}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="general_comments">Additional Comments</Label>
                <Textarea
                  id="general_comments"
                  value={formData.general_comments}
                  onChange={(e) => handleInputChange('general_comments', e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#A8D5BA] hover:bg-[#8FC9A9] text-gray-800">
                Submit Survey
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Survey Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Survey Details</DialogTitle>
          </DialogHeader>
          {selectedSurvey && (
            <div className="space-y-6">
              {/* Event Info */}
              <div>
                <h3 className="font-semibold text-lg mb-3 border-b pb-2">Event Information</h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="font-medium text-gray-600">Customer:</dt>
                    <dd>{selectedSurvey.customer_name}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-600">Job Name:</dt>
                    <dd>{humanizeDates(selectedSurvey.job_name)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-600">Trade Type:</dt>
                    <dd>{selectedSurvey.trade_type}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-600">Event Date:</dt>
                    <dd>{selectedSurvey.event_date}</dd>
                  </div>
                  {selectedSurvey.service_description && (
                    <div className="col-span-2">
                      <dt className="font-medium text-gray-600">Service Description:</dt>
                      <dd>{selectedSurvey.service_description}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Location */}
              {(selectedSurvey.service_location_name || selectedSurvey.service_address) && (
                <div>
                  <h3 className="font-semibold text-lg mb-3 border-b pb-2">Service Location</h3>
                  <dl className="space-y-2 text-sm">
                    {selectedSurvey.service_location_name && (
                      <div>
                        <dt className="font-medium text-gray-600">Location:</dt>
                        <dd>{selectedSurvey.service_location_name}</dd>
                      </div>
                    )}
                    {selectedSurvey.service_address && (
                      <div>
                        <dt className="font-medium text-gray-600">Address:</dt>
                        <dd>
                          {selectedSurvey.service_address}
                          {selectedSurvey.service_city && `, ${selectedSurvey.service_city}`}
                          {selectedSurvey.service_state && `, ${selectedSurvey.service_state}`}
                          {selectedSurvey.service_zip && ` ${selectedSurvey.service_zip}`}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {/* Ratings */}
              <div>
                <h3 className="font-semibold text-lg mb-3 border-b pb-2">Service Ratings</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Overall Service:</span>
                    <div className="flex items-center gap-2">
                      {renderStars(selectedSurvey.overall_service_rating)}
                      <span className="text-sm text-gray-600">
                        {selectedSurvey.overall_service_rating}/5
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Staff Courteous:</span>
                    <div className="flex items-center gap-2">
                      {renderStars(selectedSurvey.staff_courteous_rating)}
                      <span className="text-sm text-gray-600">
                        {selectedSurvey.staff_courteous_rating}/5
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Needs Met:</span>
                    <div className="flex items-center gap-2">
                      {renderStars(selectedSurvey.needs_met_rating)}
                      <span className="text-sm text-gray-600">
                        {selectedSurvey.needs_met_rating}/5
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feedback */}
              <div>
                <h3 className="font-semibold text-lg mb-3 border-b pb-2">Customer Feedback</h3>
                <div className="space-y-3 text-sm">
                  {selectedSurvey.what_did_you_like_1 && (
                    <div>
                      <dt className="font-medium text-gray-600">What they liked (1):</dt>
                      <dd className="mt-1 text-gray-800">{selectedSurvey.what_did_you_like_1}</dd>
                    </div>
                  )}
                  {selectedSurvey.what_did_you_like_2 && (
                    <div>
                      <dt className="font-medium text-gray-600">What they liked (2):</dt>
                      <dd className="mt-1 text-gray-800">{selectedSurvey.what_did_you_like_2}</dd>
                    </div>
                  )}
                  {selectedSurvey.what_did_you_like_3 && (
                    <div>
                      <dt className="font-medium text-gray-600">What they liked (3):</dt>
                      <dd className="mt-1 text-gray-800">{selectedSurvey.what_did_you_like_3}</dd>
                    </div>
                  )}
                  {selectedSurvey.what_to_improve && (
                    <div>
                      <dt className="font-medium text-gray-600">Areas for improvement:</dt>
                      <dd className="mt-1 text-gray-800">{selectedSurvey.what_to_improve}</dd>
                    </div>
                  )}
                  {selectedSurvey.general_comments && (
                    <div>
                      <dt className="font-medium text-gray-600">Additional comments:</dt>
                      <dd className="mt-1 text-gray-800">{selectedSurvey.general_comments}</dd>
                    </div>
                  )}
                </div>
              </div>

              {/* Survey Metadata */}
              <div>
                <h3 className="font-semibold text-lg mb-3 border-b pb-2">Survey Information</h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="font-medium text-gray-600">Survey Date:</dt>
                    <dd>{selectedSurvey.survey_date}</dd>
                  </div>
                  {selectedSurvey.survey_conducted_by && (
                    <div>
                      <dt className="font-medium text-gray-600">Conducted By:</dt>
                      <dd>{selectedSurvey.survey_conducted_by}</dd>
                    </div>
                  )}
                  {selectedSurvey.main_point_of_contact && (
                    <div>
                      <dt className="font-medium text-gray-600">Contact:</dt>
                      <dd>{selectedSurvey.main_point_of_contact}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventServiceSurveyManagement;
